// ===========================================
// Revenue Report Page
// ===========================================
// Comprehensive revenue report with filters, summary, trends, and export

import { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Alert,
} from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';

import { RevenueReportFilters } from '../../../api/reports.api';
import {
  useRevenueReport,
  useMRRTrends,
  usePaymentMethodBreakdown,
  useOutstandingInvoices,
  useExportRevenueReport,
} from '../../../hooks/useReports';
import PageHeader from '../../../components/common/PageHeader';
import RevenueFilters from '../../../components/reports/RevenueFilters';
import RevenueSummaryCards from '../../../components/reports/RevenueSummaryCards';
import MRRTrendChart from '../../../components/reports/MRRTrendChart';
import PaymentMethodChart from '../../../components/reports/PaymentMethodChart';
import RevenueByTypeChart from '../../../components/reports/RevenueByTypeChart';
import RevenueByTermChart from '../../../components/reports/RevenueByTermChart';
import OutstandingInvoicesTable from '../../../components/reports/OutstandingInvoicesTable';
import ExportButtons from '../../../components/reports/ExportButtons';

// ===========================================
// COMPONENT
// ===========================================

export default function RevenueReportPage() {
  // Filter state
  const [filters, setFilters] = useState<RevenueReportFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<RevenueReportFilters | undefined>(undefined);

  // Fetch report data (only when filters are applied)
  const { data: reportData, isLoading } = useRevenueReport(appliedFilters);
  const { data: mrrTrends, isLoading: mrrLoading } = useMRRTrends(12);
  // Outstanding invoices are their own endpoint — getRevenueReport does not
  // carry them, though this page's response type used to claim it did.
  const { data: outstandingInvoices, isLoading: outstandingLoading } = useOutstandingInvoices();
  const { data: paymentMethods, isLoading: paymentLoading } = usePaymentMethodBreakdown(appliedFilters?.termId);
  const exportMutation = useExportRevenueReport();

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters({});
    setAppliedFilters(undefined);
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    exportMutation.mutate({ format, filters: appliedFilters });
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Revenue Report"
        subtitle="Comprehensive financial analysis and revenue trends"
        icon={<MoneyIcon />}
        action={
          reportData && (
            <ExportButtons
              onExport={handleExport}
              loading={exportMutation.isPending}
              disabled={!reportData}
            />
          )
        }
      />

      {/* Filters */}
      <RevenueFilters
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State - No filters applied */}
      {!isLoading && !appliedFilters && (
        <Card>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <MoneyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Apply Filters to Generate Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a term or date range above to view revenue data
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Report Content */}
      {!isLoading && reportData && (
        <>
          {/* Summary Cards */}
          <RevenueSummaryCards summary={reportData.summary} />

          {/* Charts Grid Row 1 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* MRR Trend Chart - 8 columns */}
            <Grid item xs={12} md={8}>
              <MRRTrendChart data={mrrTrends || []} loading={mrrLoading} />
            </Grid>

            {/* Payment Method Chart - 4 columns */}
            <Grid item xs={12} md={4}>
              <PaymentMethodChart data={paymentMethods || []} loading={paymentLoading} />
            </Grid>
          </Grid>

          {/* Charts Grid Row 2 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Revenue by Term Chart - 6 columns */}
            <Grid item xs={12} md={6}>
              <RevenueByTermChart data={reportData.byTerm || []} />
            </Grid>

            {/* Revenue by Type Chart - 6 columns */}
            <Grid item xs={12} md={6}>
              <RevenueByTypeChart data={reportData.byLessonType || []} />
            </Grid>
          </Grid>

          {/* Outstanding Invoices Section */}
          {outstandingInvoices && outstandingInvoices.length > 0 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Outstanding Invoices
                  </Typography>
                </Box>
                <OutstandingInvoicesTable invoices={outstandingInvoices} loading={outstandingLoading} />
              </CardContent>
            </Card>
          )}

          {/* No Outstanding Invoices */}
          {outstandingInvoices && outstandingInvoices.length === 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              All invoices have been paid. No outstanding balances.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
